import React, { useEffect, useMemo, useState } from "react";
import { fnGetDataSet } from "@shared/api/restApi";
import { global } from "@shared/libs/Global";
import {
  fnFormatNumberByPattern,
  formatNumber,
  toNum,
} from "@shared/utils/CommonUtils";
import SnChart from "@shared/components/Grid/SnChart";
import { snSysWidget013 } from "./icons";

interface Props {
  cdWidget?: string;
  nmWidget?: string;
  searchData?: any;
  refreshKey?: number;
}

// sn_sys_widget_009(생산실적현황) 기반. 불량율 추가, 양품/불량 누적 막대 + 불량률 선(오른쪽 축).
// rt_b 는 비율(불량/생산)로 오므로 화면에서 100을 곱해 %로 표시한다.
const SnSysWidget013: React.FC<Props> = ({
  nmWidget,
  searchData,
  refreshKey,
}) => {
  const { userInfo } = global();
  const [summary, setSummary] = useState<any>({});
  const [trendRows, setTrendRows] = useState<any[]>([]);

  const params = useMemo(
    () => ({
      cd_firm: searchData?.cd_firm || userInfo.cd_firm,
      dt: searchData?.dt,
    }),
    [searchData?.cd_firm, searchData?.dt, userInfo.cd_firm]
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data: any = await fnGetDataSet({
          id: "sn_sys_widget_013_01",
          params,
        });

        setSummary(data?.[0]?.[0] || {});
        setTrendRows(data?.[1] || []);
      } catch (error) {
        setSummary({});
        setTrendRows([]);
      }
    };

    fetchData();
  }, [params, refreshKey]);

  const summaryCols = [
    {
      label: "생산실적량",
      value: formatNumber(toNum(summary?.qt), { fractionDigits: 0 }),
    },
    {
      label: "양품실적량",
      value: formatNumber(toNum(summary?.qt_w), { fractionDigits: 0 }),
    },
    {
      label: "불량실적량",
      value: formatNumber(toNum(summary?.qt_b), { fractionDigits: 0 }),
    },
    {
      label: "불량율",
      value: `${fnFormatNumberByPattern(toNum(summary?.rt_b) * 100, "#,##0.#")}%`,
    },
  ];

  const chartData = useMemo(
    () =>
      trendRows.map((row) => ({
        ...row,
        cd_day: row?.cd_day || "",
        dt_run: row?.dt_run || row?.cd_day || "",
        qt_w: toNum(row?.qt_w),
        qt_b: toNum(row?.qt_b),
        rt_b: toNum(row?.rt_b) * 100,
      })),
    [trendRows]
  );

  const qtyTooltip = {
    renderer: ({ datum, yKey, yName }: any) => ({
      title: datum?.dt_run || datum?.cd_day || "",
      content: `${yName} ${formatNumber(toNum(datum?.[yKey]), {
        fractionDigits: 0,
      })} EA`,
    }),
  };

  return (
    <div className="flex h-full min-h-[280px] w-full flex-col rounded-lg border border-gray-100 bg-white px-4 py-3 shadow-sm">
      <div className="mb-4 flex items-center gap-3">
        <div className="aspect-square h-10 shrink-0">
          <img
            src={snSysWidget013}
            alt=""
            className="h-full w-full object-contain"
          />
        </div>
        <div className="truncate text-xl font-bold text-slate-900">{nmWidget}</div>
      </div>

      <table className="w-full table-fixed border-collapse text-center text-sm">
        <thead>
          <tr className="bg-gray-100">
            {summaryCols.map((col) => (
              <th
                key={col.label}
                className="truncate border border-gray-300 px-1 py-1 font-medium text-slate-800"
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {summaryCols.map((col) => (
              <td
                key={col.label}
                className="truncate border border-gray-300 px-1 py-1 text-slate-900"
              >
                {col.value}
              </td>
            ))}
          </tr>
        </tbody>
      </table>

      <div className="mt-4 text-sm font-semibold text-slate-800">
        생산량추이
      </div>
      <div className="min-h-0 flex-1 overflow-visible pb-2">
        <SnChart
          type="custom"
          data={chartData}
          height="100%"
          width="100%"
          xKey="cd_day"
          yKey="qt_w"
          seriesOptions={{
            series: [
              {
                type: "bar",
                direction: "vertical",
                xKey: "cd_day",
                yKey: "qt_w",
                yName: "양품",
                stacked: true,
                fill: "#10b981",
                stroke: "#059669",
                tooltip: qtyTooltip,
              },
              {
                type: "bar",
                direction: "vertical",
                xKey: "cd_day",
                yKey: "qt_b",
                yName: "불량",
                stacked: true,
                fill: "#ef4444",
                stroke: "#dc2626",
                tooltip: qtyTooltip,
              },
              {
                type: "line",
                xKey: "cd_day",
                yKey: "rt_b",
                yName: "불량률",
                stroke: "#f59e0b",
                strokeWidth: 2,
                marker: { enabled: true, size: 5, fill: "#f59e0b" },
                tooltip: {
                  renderer: ({ datum, yKey, yName }: any) => ({
                    title: datum?.dt_run || datum?.cd_day || "",
                    content: `${yName} ${formatNumber(toNum(datum?.[yKey]), {
                      fractionDigits: 1,
                    })} %`,
                  }),
                },
              },
            ],
          }}
          options={{
            padding: { top: 6, right: 10, bottom: 12, left: 6 },
            legend: {
              enabled: true,
              position: "bottom",
              item: { label: { fontSize: 11 } },
            },
            axes: [
              {
                type: "category",
                position: "bottom",
                label: { fontSize: 12 },
              },
              {
                type: "number",
                position: "left",
                keys: ["qt_w", "qt_b"],
                label: { enabled: false },
                nice: true,
              },
              {
                type: "number",
                position: "right",
                keys: ["rt_b"],
                min: 0,
                nice: true,
                label: {
                  fontSize: 11,
                  formatter: ({ value }: any) => `${value}%`,
                },
              },
            ],
          }}
        />
      </div>
    </div>
  );
};

export default SnSysWidget013;
